import type { ReactNode } from "react"
import { useTranslation } from "@/i18n"
import { Modal } from "@/components/Modal"

export interface FilterDrawerProps {
  open: boolean
  title: string
  description?: string
  /** Number of selections the pending state would apply. */
  activeCount: number
  onApply: () => void
  onCancel: () => void
  onClearAll: () => void
  children: ReactNode
}

/**
 * The filter panel.
 *
 * It reuses the accessible `Modal` (focus trap, Escape, scroll lock) so the
 * drawer behaves like every other dialog of the client, and it edits a **draft**:
 * nothing is applied until the reader presses Apply, and Cancel restores the
 * previous state.
 */
export function FilterDrawer({
  open,
  title,
  description,
  activeCount,
  onApply,
  onCancel,
  onClearAll,
  children,
}: FilterDrawerProps) {
  const { t } = useTranslation()

  return (
    <Modal
      open={open}
      onClose={onCancel}
      title={title}
      description={description}
      size="large"
      className="filter-drawer"
      footer={
        <div className="filter-drawer-actions">
          <button type="button" className="btn btn-ghost" onClick={onClearAll}>
            {t("Clear all")}
          </button>
          <div className="filter-drawer-actions-main">
            <button type="button" className="btn btn-ghost" onClick={onCancel}>
              {t("Cancel")}
            </button>
            <button type="button" className="btn btn-primary" onClick={onApply}>
              {activeCount > 0 ? t("Apply ({n} active)", { n: activeCount }) : t("Apply")}
            </button>
          </div>
        </div>
      }
    >
      <div className="filter-drawer-body">{children}</div>
    </Modal>
  )
}
